import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router'

import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import { SidebarProvider } from '@/components/ui'
import { NavMain, type NavItem } from '@/components/ui/sidebar/nav-main'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenuButton,
  SidebarRail,
} from '@/components/ui/sidebar/sidebar'
import { useAtendimentoAtivo } from '@/features/atendimento/shared/hooks/use-atendimento-ativo'
import { RegionalSwitcher } from '@/features/regionais/components/regional-switcher'
import { useAuth } from '@/hooks'
import { useAtendimento } from '@/hooks/use-atendimento'
import { showToast } from '@/infra/sonner'
import { DestructiveActionGuard } from '@/shared/destructive-action-guard'

export const AppSidebarChile = ({ ...props }: React.ComponentProps<typeof Sidebar>) => {
  const { t } = useTranslation(['common', 'login'])
  const { user, logout } = useAuth()
  const { atendimento, isEmAtendimento } = useAtendimento()
  useAtendimentoAtivo(Boolean(user?.unidadeDeNegocioId))

  const handleOpenSimulacaoRapida: NonNullable<NavItem['onClick']> = (event) => {
    if (!isEmAtendimento) return

    event.preventDefault()
    showToast({
      message: t('layouts.sidebar.simulacao_rapida_sem_permissao'),
      variant: 'error',
      closeButton: true,
    })
  }

  const navMainItems: NavItem[] = [
    {
      title: t('layouts.sidebar.empreendimentos'),
      url: '/es-CL/empreendimentos',
      icon: 'building-2',
    },
    {
      title: t('layouts.sidebar.espelho_de_vendas'),
      url: '/es-CL/espelho-de-vendas',
      icon: 'floor-plan',
    },
    {
      title: t('layouts.sidebar.simulacao_rapida'),
      url: '/es-CL/simulacao-rapida',
      icon: 'sparkles',
      onClick: handleOpenSimulacaoRapida,
    },
    {
      title: t('layouts.sidebar.historico'),
      url: '/es-CL/historico',
      icon: 'scroll-text',
      children: [
        { title: t('layouts.sidebar.simulacoes'), url: '/es-CL/historico/simulacoes' },
        { title: t('layouts.sidebar.simulacoes_rapidas'), url: '/es-CL/historico/simulacoes-rapidas' },
        { title: t('layouts.sidebar.propostas'), url: '/es-CL/historico/propostas' },
      ],
    },
  ]

  return (
    <SidebarProvider>
      <Sidebar data-testid="app-sidebar" collapsible="icon" {...props}>
        <SidebarHeader>
          <Render.If condition={user}>
            {(_user) => <RegionalSwitcher user={_user} isEmAtendimento={Boolean(atendimento?.id)} />}
          </Render.If>
        </SidebarHeader>
        <SidebarContent>
          <NavMain items={navMainItems} />
        </SidebarContent>
        <SidebarFooter>
          <DestructiveActionGuard
            title={t('login:dialog.logout.title')}
            description={t('login:dialog.logout.description')}
          >
            <SidebarMenuButton className="text-destructive" tooltip={t('layouts.sidebar.sair')} onClick={logout}>
              <Icon name="logout" />
              {t('layouts.sidebar.sair')}
            </SidebarMenuButton>
          </DestructiveActionGuard>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      <SidebarInset>
        <Outlet />
      </SidebarInset>
    </SidebarProvider>
  )
}
